/**
 * Tag Repository — CRUD untuk tabel tag_cache + activity_tag_cache (junction).
 *
 * Tag adalah label bebas yang bisa ditempel pada aktivitas (many-to-many).
 * Berbeda dari kategori, tag tidak terstruktur — user bebas membuat tag apa
 * saja. Tag bersifat workspace-scoped (unik per workspace+nama).
 *
 * Acuan: docs/04-Database.md §6 (tag_cache, activity_tag_cache),
 *        docs/01-SRS.md FR-ACT-05.
 */

import { BaseRepository } from '../repository';
import type { TagCache, SyncStatus } from '../types';

/** Input untuk membuat tag baru. */
export interface CreateTagInput {
  workspace_id: string;
  tenant_id: string;
  name: string;
  color?: string;
}

/** Palet warna auto-generate untuk tag baru tanpa warna eksplisit. */
const TAG_COLOR_PALETTE = [
  '#C9A227', // gold
  '#0EA5E9', // sky
  '#16A34A', // green
  '#DC2626', // red
  '#7C3AED', // violet
  '#EA580C', // orange
  '#0891B2', // cyan
  '#DB2777', // pink
];

export class TagRepository extends BaseRepository<TagCache> {
  protected readonly tableName = 'tag_cache';

  protected mapRow(row: Record<string, unknown>): TagCache {
    return row as unknown as TagCache;
  }

  // -------------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------------

  async create(data: CreateTagInput): Promise<TagCache> {
    const id = this.generateId();
    const now = this.now();

    this.insertRow({
      id,
      workspace_id: data.workspace_id,
      tenant_id: data.tenant_id,
      name: data.name,
      color: data.color ?? this.pickColor(data.name),
      sync_status: 'pending',
      last_synced_at: null,
      updated_at: now,
    });

    const created = await this.findById(id);
    if (!created) throw new Error(`Failed to create tag: ${id}`);
    return created;
  }

  // -------------------------------------------------------------------------
  // Specialized Queries
  // -------------------------------------------------------------------------

  /** Ambil semua tag dalam workspace, urut alphabetically. */
  async findByWorkspace(workspaceId: string): Promise<TagCache[]> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
      ORDER BY name ASC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Cari tag berdasarkan nama (case-insensitive) dalam workspace. */
  async findByName(workspaceId: string, name: string): Promise<TagCache | null> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ? AND LOWER(name) = LOWER(?)
      LIMIT 1
    `;
    const row = this.db.queryOne<Record<string, unknown>>(sql, [workspaceId, name]);
    return row ? this.mapRow(row) : null;
  }

  /**
   * Find or create — helper untuk TagInput component.
   * Jika tag dengan nama tersebut sudah ada, return yang ada.
   * Jika belum, buat baru. Case-insensitive match.
   */
  async findOrCreate(workspaceId: string, tenantId: string, name: string): Promise<TagCache> {
    const trimmed = name.trim();
    const existing = await this.findByName(workspaceId, trimmed);
    if (existing) return existing;
    return this.create({ workspace_id: workspaceId, tenant_id: tenantId, name: trimmed });
  }

  /** Ambil tag dengan sync_status tertentu (untuk sync engine). */
  async findBySyncStatus(status: SyncStatus): Promise<TagCache[]> {
    const sql = `SELECT * FROM ${this.tableName} WHERE sync_status = ?`;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [status]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Tandai tag sebagai tersinkron. */
  async markSynced(id: string): Promise<void> {
    this.updateRow(id, {
      sync_status: 'synced',
      last_synced_at: this.now(),
    });
  }

  // -------------------------------------------------------------------------
  // Activity ↔ Tag Junction (activity_tag_cache)
  // -------------------------------------------------------------------------

  /**
   * Ambil semua tag yang terpasang pada aktivitas tertentu.
   * Join activity_tag_cache dengan tag_cache.
   */
  async getTagsForActivity(activityId: string): Promise<TagCache[]> {
    const sql = `
      SELECT t.* FROM tag_cache t
      INNER JOIN activity_tag_cache at ON at.tag_id = t.id
      WHERE at.activity_id = ?
      ORDER BY t.name ASC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [activityId]);
    return rows.map((row) => this.mapRow(row));
  }

  /**
   * Sinkronkan tag-tag pada aktivitas (diff-based).
   * Bandingkan tag yang ada dengan tag yang diinginkan, lalu insert/delete
   * hanya perbedaannya (lebih efisien daripada delete-all + insert-all).
   *
   * @param activityId ID aktivitas.
   * @param tagIds Array ID tag yang ingin dipasang.
   */
  async setTagsForActivity(activityId: string, tagIds: string[]): Promise<void> {
    const current = await this.getTagsForActivity(activityId);
    const currentIds = new Set(current.map((t) => t.id));
    const desiredIds = new Set(tagIds);

    // Tag yang perlu ditambahkan (ada di desired, tidak ada di current)
    for (const tagId of desiredIds) {
      if (!currentIds.has(tagId)) {
        this.db.run(`INSERT INTO activity_tag_cache (activity_id, tag_id) VALUES (?, ?)`, [
          activityId,
          tagId,
        ]);
      }
    }

    // Tag yang perlu dihapus (ada di current, tidak ada di desired)
    for (const tagId of currentIds) {
      if (!desiredIds.has(tagId)) {
        this.db.run(`DELETE FROM activity_tag_cache WHERE activity_id = ? AND tag_id = ?`, [
          activityId,
          tagId,
        ]);
      }
    }
  }

  /** Hapus satu tag dari aktivitas. */
  async removeTagFromActivity(activityId: string, tagId: string): Promise<void> {
    this.db.run(`DELETE FROM activity_tag_cache WHERE activity_id = ? AND tag_id = ?`, [
      activityId,
      tagId,
    ]);
  }

  /** Hapus semua tag dari aktivitas (dipakai saat soft-delete aktivitas). */
  async clearTagsForActivity(activityId: string): Promise<void> {
    this.db.run(`DELETE FROM activity_tag_cache WHERE activity_id = ?`, [activityId]);
  }

  // -------------------------------------------------------------------------
  // Private Helpers
  // -------------------------------------------------------------------------

  /**
   * Pilih warna dari palet berdasarkan hash nama tag.
   * Memastikan tag dengan nama yang sama selalu dapat warna yang sama.
   */
  private pickColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % TAG_COLOR_PALETTE.length;
    return TAG_COLOR_PALETTE[index];
  }
}
